// Auto-generated by splitter.py — do not regenerate; edit directly.

import React, { useId } from 'react';
import { Chip } from './Chip';
import { Picker } from './Picker';

export const Toggle = ({ on, label, sub, locked }) => (
  <div className="flex items-center justify-between gap-4 py-2.5">
    <div className="min-w-0">
      <div className="text-[12.5px] font-medium text-[#1F2937]">{label}</div>
      {sub && <div className="text-[11px] text-[#6B7280] mt-0.5">{sub}</div>}
    </div>
    <div className="flex items-center gap-2 flex-shrink-0">
      {locked && <span className="text-[10.5px] text-[#9CA3AF] uppercase tracking-wider">Locked</span>}
      <div className={`w-8 h-4 rounded-full relative transition-colors ${on ? 'bg-[#1F2937]' : 'bg-[#D6D3D1]'} ${locked ? 'opacity-60' : ''}`}>
        <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${on ? 'left-[18px]' : 'left-0.5'}`} />
      </div>
    </div>
  </div>
);

// Editable settings row — inline state with edit affordance

// `disabled` is a real state here, not decoration: a choice the system cannot honour must look and
// behave unavailable, and say why on hover, rather than being silently hidden (which leaves the next
// reader to rediscover the reason) or left clickable (which is how an operator ends up believing
// they chose something). `title` carries the reason — see reason-on-hover across the console.
export const RadioCard = ({ selected, onClick, label, sub, suffix, disabled = false, title }) => (
  <button
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    title={title || (disabled && typeof sub === 'string' ? sub : undefined)}
    className={`w-full text-left px-4 py-3 rounded-md border transition-colors ${
    disabled ? 'border-[#EEEDEA] bg-[#FBFAF7] cursor-not-allowed opacity-70'
    : selected ? 'border-[#1F2937] border-2 bg-[#FBFAF7]' : 'border-[#E6E4E1] bg-white hover:border-[#D6D3D1]'
  }`}>
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <div className={`w-4 h-4 rounded-full mt-0.5 flex-shrink-0 flex items-center justify-center ${selected ? 'bg-[#1F2937]' : 'border-2 border-[#D6D3D1]'}`}>
          {selected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className={`text-[13px] font-semibold ${disabled ? 'text-[#9CA3AF]' : 'text-[#1F2937]'}`}>{label}</div>
          {sub && <div className="text-[11.5px] text-[#6B7280] mt-0.5">{sub}</div>}
        </div>
      </div>
      {suffix && <div className="text-[12.5px] font-medium text-[#1F2937] flex-shrink-0">{suffix}</div>}
    </div>
  </button>
);

// Field label + input (form pattern)

// Every Field labels its control for real — a generated id flows onto the single
// form-control child (Input/Select/Textarea all spread ...rest onto their native
// element, so this needs no change at any of the hundreds of call sites) and the
// label becomes a real <label htmlFor>, not a bare <div> a screen reader skips.
export const Field = ({ label, optional, required, children, sub }) => {
  const generatedId = useId();
  const child = React.isValidElement(children) ? children : null;
  const controlId = child ? (child.props.id || generatedId) : undefined;
  const labelledChildren = child ? React.cloneElement(child, { id: controlId }) : children;
  return (
    <div>
      <label htmlFor={controlId} className="block text-[10.5px] font-semibold tracking-[0.06em] uppercase text-[#6B7280] mb-1.5">
        {label}
        {required && <span className="font-normal normal-case ml-1 text-[#9B1F1F]">*</span>}
        {optional && <span className="font-normal normal-case ml-1 text-[#9CA3AF]">(optional)</span>}
      </label>
      {labelledChildren}
      {sub && <div className="text-[11px] text-[#6B7280] mt-1">{sub}</div>}
    </div>
  );
};

// Text input, consistent with the rest of the system.

/**
 * WHAT THE BROWSER IS TOLD A FIELD IS, derived from its `type` so it is stated once.
 *
 * A box with no `name` and no `autoComplete` is matched by the browser's own heuristics, and
 * Chrome then offers its form history for every other unnamed box in the console: the phone field
 * on the manual-order modal was suggesting "Ntugasaze FC", "VIP" and "Zimbabwe vs D.R Congo".
 * Naming them also gets the right keyboard on a phone and the real contact autofill an operator
 * taking a booking over the counter actually wants.
 *
 * Spread BEFORE `rest`, so any caller can still override a token.
 */
const AUTOFILL_BY_TYPE = {
  email: { name: 'email', autoComplete: 'email', inputMode: 'email', autoCapitalize: 'none', spellCheck: false },
  tel: { name: 'tel', autoComplete: 'tel', inputMode: 'tel' },
};

// size="sm": the 32px control of dense work surfaces (the venue module's dialogs and panels), with
// phone-sized text (no zoom on focus) and a keyboard focus ring. `className` is ADDED to the control's
// classes. Forwards its ref. (Merged from the venue module's kit.)
const SM_CONTROL = 'h-8 w-full min-w-0 border border-[#E6E4E1] rounded-[6px] px-2.5 text-[16px] md:text-[12.5px] text-[#1F2937] bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1F2937]/40 focus:border-[#9CA3AF] disabled:bg-[#FBFAF7] disabled:text-[#6B7280] disabled:cursor-not-allowed';

/** Controlled with `onChange`; read-only with a `value` and no `onChange`; otherwise its own (`defaultValue`). */
const valueProps = (value, onChange) => (onChange
  ? { value: value !== undefined ? value : '', onChange }
  : value !== undefined
    ? { value, readOnly: true }
    : {});

export const Input = React.forwardRef(({ value, onChange, placeholder, type = 'text', disabled, size, className = '', ...rest }, ref) => (
  <input
    ref={ref}
    type={type}
    {...(AUTOFILL_BY_TYPE[type] || {})}
    {...valueProps(value, onChange)}
    placeholder={placeholder}
    disabled={disabled}
    className={`${size === 'sm' ? SM_CONTROL : `w-full h-9 px-3 text-[12.5px] border border-[#E6E4E1] rounded-md focus:outline-none focus:border-[#1F2937] ${disabled ? 'bg-[#FBFAF7] text-[#6B7280] cursor-not-allowed' : 'bg-white'}`} ${className}`}
    {...rest}
  />
));
Input.displayName = 'Input';

// Textarea

// ⌘/Ctrl + Enter submits the composer a textarea belongs to.
//
// Plain Enter can't be the send key here: these are multi-line message composers, and an
// operator writing a two-line reply to a customer would fire it off half-written. The
// modified chord is the convention every chat tool the operator already uses shares
// (WhatsApp Web, Slack in send-on-⌘↵ mode, GitHub).
//
// `disabled` is not optional politeness — pass the SAME condition the Send button uses, so
// the shortcut can never fire a send the button itself refuses (empty body, request already
// in flight, closed session window). A shortcut that bypasses the button's guard is a way to
// double-send a real WhatsApp message to a real customer.
export const submitOnCmdEnter = (submit, { disabled = false } = {}) => (e) => {
  if (e.key !== 'Enter' || !(e.metaKey || e.ctrlKey)) return;
  e.preventDefault();
  if (disabled || typeof submit !== 'function') return;
  submit();
};

export const Textarea = React.forwardRef(({ value, onChange, placeholder, rows = 3, size, className = '', ...rest }, ref) => (
  <textarea
    ref={ref}
    {...valueProps(value, onChange)}
    placeholder={placeholder}
    rows={rows}
    className={`${size === 'sm' ? `${SM_CONTROL} !h-auto py-1.5 resize-y` : 'w-full px-3 py-2 text-[12.5px] bg-white border border-[#E6E4E1] rounded-md focus:outline-none focus:border-[#1F2937] resize-y'} ${className}`}
    {...rest}
  />
));
Textarea.displayName = 'Textarea';

// Select — native dropdown

/**
 * WHERE SOMETHING IS — and never a guess at where.
 *
 * Four separate country pickers in this console opened on "Uganda": the venue wizard, the team
 * create form, the team edit form, the new-customer block in manual orders. The console serves six
 * tenants and this one's organizers are Kenyan football clubs, so a prefilled country is not a
 * convenience, it is an assertion nobody made — and it persists. A Nairobi ground was saved as
 * Kampala, Uganda; a team edit form had already been patched once for letting a display sentinel
 * leak into the column and STILL fell back to Uganda underneath the patch.
 *
 * `normalizeCustomer` states the rule the reading side already follows: *"never a guessed
 * 'Uganda' — the platform sells in at least Uganda and Kenya, so a default here labels every
 * country-less customer as Ugandan and the console has no way to tell the difference between
 * that and one who told us."* This is the writing side of the same rule, in one component, so a
 * fifth picker cannot quietly reintroduce the default: `''` means not stated, it is the initial
 * option, and there is nothing for a caller to prefill.
 */
export const COUNTRIES = ['Uganda', 'Kenya', 'Tanzania', 'Rwanda', 'Burundi', 'South Sudan', 'Ethiopia', 'Other'];

export const CountrySelect = ({ value, onChange, ...rest }) => (
  <Select value={value || ''} onChange={onChange} {...rest}>
    <option value="">Not stated</option>
    {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
  </Select>
);

// SELECT: one control for every choice in the console, the console's Picker (ui/Picker.jsx) behind a
// native <select>'s props, so a caller written for the native element is unchanged. Merged from the venue
// module's kit (src/modules/venue-port), whose Select already was this; the native look it replaces could
// not search, group, hint, say why an option is off, or look the same on every platform.
//   options      strings or { value, label, disabled }, or <option> children (in <optgroup>s: groups)
//   onChange     receives an event-shaped { target: { value, name } }
//   placeholder  the empty choice: shown when nothing is chosen, and the × returns to it. Without one, an
//                <option value=""> keeps its label ("All kinds") and stays in the list
//   size         xs (28px) | sm (32px) | 'inline' (24px, a choice inside a row) | default (36px)
//   searchable   default: on past ten options (the Picker's rule)
//   warn         amber value text, for a value that needs review
//   className    sizes the control (it lands on the Picker's wrapper); with no width class the control is
//                as wide as its longest option, as a native select is
//   title        what the control is; when it is disabled, why
//   name         posted with a form through a hidden input
const PICKER_SIZE = { xs: 'sm', sm: 'bar', inline: 'xs' };
// The trigger's font size by Select size, set on the wrapper too so the native-width estimate (in ch) matches it.
const SIZE_FONT = { inline: 'text-[10.5px]', xs: 'text-[11.5px]', sm: 'text-[12px]' };
const optionText = (children) => React.Children.toArray(children).map((c) => (typeof c === 'string' || typeof c === 'number' ? String(c) : '')).join('');
const optionsOf = (options, children) => {
  if (options) return options.map((o) => (typeof o === 'string' ? { value: o, label: o } : { value: o.value, label: o.label, disabled: o.disabled, hint: o.hint, group: o.group }));
  const out = [];
  const walk = (nodes, group) => React.Children.forEach(nodes, (n) => {
    if (!React.isValidElement(n)) return;
    if (n.type === 'option') out.push({ value: n.props.value ?? optionText(n.props.children), label: optionText(n.props.children), disabled: !!n.props.disabled, ...(group ? { group } : {}) });
    else if (n.type === 'optgroup') walk(n.props.children, n.props.label);
    else if (n.props && n.props.children) walk(n.props.children, group);
  });
  walk(children, null);
  return out;
};
const HAS_WIDTH = /(^|\s)(w-|min-w-|flex-1|basis-|grow)/;

export const Select = React.forwardRef((props, ref) => {
  const { value, defaultValue, onChange, options, placeholder, disabled, children, size, warn, variant, searchable, className = '', title, name, id, ...rest } = props;
  const all = React.useMemo(() => optionsOf(options, children), [options, children]);
  // The empty choice: the caller's placeholder, else the label of an <option value=""> (kept in the list).
  const emptyOpt = all.find((o) => String(o.value) === '');
  const opts = placeholder ? all.filter((o) => String(o.value) !== '') : all;
  const controlled = 'value' in props;
  const [own, setOwn] = React.useState(defaultValue ?? '');
  const current = String((controlled ? value : own) ?? '');
  const choose = (v) => {
    const next = v == null ? '' : String(v);
    if (!controlled) setOwn(next);
    if (onChange) onChange({ target: { value: next, name }, currentTarget: { value: next, name } });
  };
  // A native select is as wide as its longest option; so is this, unless the caller sizes it.
  const longest = HAS_WIDTH.test(className) ? 0 : all.reduce((n, o) => Math.max(n, String(o.label ?? '').length), String(placeholder || '').length);
  const dataProps = Object.fromEntries(Object.entries(rest).filter(([k]) => k.startsWith('data-')));
  return (
    <span ref={ref} className="contents" title={title} {...dataProps}>
      <Picker
        value={current}
        onChange={choose}
        options={opts}
        placeholder={placeholder || (emptyOpt ? emptyOpt.label : 'Choose…')}
        clearable={!!placeholder}
        searchable={searchable}
        disabled={disabled}
        disabledReason={disabled ? title : undefined}
        size={PICKER_SIZE[size] || 'md'}
        variant={variant || 'field'}
        id={id}
        ariaLabel={rest['aria-label'] || title}
        className={`${longest ? `inline-block ${SIZE_FONT[size] || 'text-[12.5px]'}` : ''} ${warn ? '[&_button]:text-[#8A5A12]' : ''} ${className}`.trim()}
        // ~0.9ch per character of the trigger's font, plus its padding and chevron (2.25rem).
        style={longest ? { minWidth: `calc(${(Math.min(longest, 40) * 0.9).toFixed(1)}ch + 2.25rem)` } : undefined}
      />
      {name && <input type="hidden" name={name} value={current} />}
    </span>
  );
});
Select.displayName = 'Select';

// Slider — range with label

export const Slider = ({ value, onChange, min = 0, max = 100, step = 1, suffix = '%' }) => (
  <div className="flex items-center gap-3">
    <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange && onChange(Number(e.target.value))}
      className="flex-1 h-1.5 bg-[#E6E4E1] rounded-full appearance-none cursor-pointer accent-[#1F2937]" />
    <div className="h-8 w-16 rounded-md border border-[#E6E4E1] bg-white text-[12.5px] text-[#1F2937] font-medium tabular-nums flex items-center justify-center flex-shrink-0">
      {value}{suffix}
    </div>
  </div>
);

// Stateful toggle — used in settings (visually identical to Toggle but clickable)

export const ToggleControl = ({ value, onChange, label, sub, locked, badge }) => (
  <button onClick={() => !locked && onChange && onChange(!value)} disabled={locked} className={`w-full flex items-start justify-between gap-3 px-4 py-3 rounded-md border ${value ? 'border-[#1F2937] bg-white' : 'border-[#E6E4E1] bg-white'} ${locked ? 'opacity-60 cursor-not-allowed' : 'hover:border-[#D6D3D1]'} text-left transition-colors`}>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <span className="text-[12.5px] font-medium text-[#1F2937]">{label}</span>
        {badge && <Chip tone="neutral" size="sm">{badge}</Chip>}
      </div>
      {sub && <div className="text-[11px] text-[#6B7280] mt-0.5">{sub}</div>}
    </div>
    <div className={`w-8 h-4 rounded-full relative transition-colors flex-shrink-0 mt-1 ${value ? 'bg-[#1F6A3A]' : 'bg-[#D6D3D1]'}`}>
      <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${value ? 'left-[18px]' : 'left-0.5'}`} />
    </div>
  </button>
);


/* ============================================================================
   PHASE 3 — REFUND WIZARD + VIEW ORDER + FLAG TRANSACTION MODALS
   ============================================================================ */

